import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { typeRole } from "../../utils/typography";
import { Button } from "../Button";
import { Heading } from "../Heading";
import { Icon, type IconName } from "../Icon";
import { TextLink } from "../TextLink";

export type EmptyStateSize = "sm" | "md" | "lg";
export type EmptyStateAlignment = "center" | "start";
type EmptyStateVariant = "default" | "spotlight";
type EmptyStateSpotlightPattern = "mesh" | "top-fade" | "center" | "corners" | "bottom" | "asymmetric";

export interface EmptyStateAction {
  /** Label for the action button/link */
  label: string;
  /** Click handler for the action */
  onPress?: () => void;
  /** Optional href for link-style actions */
  href?: string;
  /** Whether the action is loading */
  isLoading?: boolean;
  /** Whether the action is disabled */
  isDisabled?: boolean;
}

export interface EmptyStateProps {
  /** Icon name from Icon component to show at the top of the empty state. */
  icon?: IconName;
  /** Primary title describing the state. */
  title: ReactNode;
  /** Optional description providing more context. */
  description?: ReactNode;
  /** Primary action (rendered as a button). */
  primaryAction?: EmptyStateAction;
  /** Secondary action (rendered as a text link). */
  secondaryAction?: EmptyStateAction;
  /** Optional custom action area. Use this for complete control over actions. Overrides primaryAction and secondaryAction. */
  actions?: ReactNode;
  /** Controls the component padding and typography. */
  size?: EmptyStateSize;
  /** Aligns the content horizontally. */
  alignment?: EmptyStateAlignment;
  /** Visual variant: 'default' for minimal style, 'spotlight' for branded marketing style. */
  variant?: EmptyStateVariant;
  /** Gradient pattern for spotlight variant. Only applies when variant="spotlight". */
  spotlightPattern?: EmptyStateSpotlightPattern;
  /** When true the component expands to fill the available height and vertically centers content. */
  fullHeight?: boolean;
  /** Additional className to merge with default styles. */
  className?: string;
}

const sizeStyles: Record<EmptyStateSize, string> = {
  sm: "gap-4 px-6 py-8",
  md: "gap-5 px-8 py-12",
  lg: "gap-6 px-10 py-16",
};

const titleSizeMap: Record<EmptyStateSize, "xs" | "sm" | "md"> = {
  sm: "xs",
  md: "sm",
  lg: "md",
};

const descriptionStyles: Record<EmptyStateSize, string> = {
  sm: typeRole("bodySm"),
  md: typeRole("bodySm"),
  lg: typeRole("bodyMd"),
};

const iconContainerStyles: Record<EmptyStateSize, string> = {
  sm: "h-10 w-10",
  md: "h-12 w-12",
  lg: "h-16 w-16",
};

const iconSizeMap: Record<EmptyStateSize, number> = {
  sm: 20,
  md: 24,
  lg: 32,
};

const alignmentStyles: Record<EmptyStateAlignment, string> = {
  center: "items-center text-center [&_.empty-state-actions]:justify-center [&_.empty-state-actions]:text-center",
  start: "items-start text-left [&_.empty-state-actions]:justify-start [&_.empty-state-actions]:text-left",
};

const spotlightGradients: Record<EmptyStateSpotlightPattern, string> = {
  mesh: "radial-gradient(at 20% 20%, rgba(68, 74, 225, 0.06) 0px, transparent 45%), radial-gradient(at 80% 80%, rgba(68, 74, 225, 0.045) 0px, transparent 45%), radial-gradient(at 50% 50%, rgba(68, 74, 225, 0.03) 0px, transparent 50%)",
  "top-fade": "linear-gradient(to bottom, rgba(68, 74, 225, 0.06) 0%, rgba(68, 74, 225, 0.02) 60%, transparent 100%)",
  center: "radial-gradient(ellipse 600px 400px at center, rgba(68, 74, 225, 0.06) 0px, transparent 60%)",
  corners:
    "radial-gradient(at 0% 0%, rgba(68, 74, 225, 0.06) 0px, transparent 40%), radial-gradient(at 100% 100%, rgba(68, 74, 225, 0.045) 0px, transparent 40%)",
  bottom: "radial-gradient(ellipse 700px 300px at 50% 100%, rgba(68, 74, 225, 0.06) 0px, transparent 60%)",
  asymmetric:
    "radial-gradient(at 15% 30%, rgba(68, 74, 225, 0.06) 0px, transparent 45%), radial-gradient(at 85% 70%, rgba(68, 74, 225, 0.04) 0px, transparent 50%)",
};

/**
 * EmptyState
 *
 * Minimal, composable empty state surface for communicating lack of data.
 * Supports optional icon, description and opinionated action patterns.
 *
 * Use `primaryAction` (button) and `secondaryAction` (text link) for consistent styling,
 * or use `actions` for complete control over the action area.
 *
 * The `spotlight` variant adds a subtle branded gradient background for marketing-focused
 * empty states that encourage user behavior. Choose from different gradient patterns
 * using the `spotlightPattern` prop.
 */
export function EmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  actions,
  size = "md",
  alignment = "center",
  variant = "default",
  spotlightPattern = "mesh",
  fullHeight = false,
  className,
}: EmptyStateProps) {
  const buttonSize = size === "sm" ? "sm" : "md";
  const isSpotlight = variant === "spotlight";
  const spotlightStyle = isSpotlight ? { background: spotlightGradients[spotlightPattern] } : undefined;

  const renderActions = () => {
    // If custom actions provided, use those
    if (actions) {
      return <div className="empty-state-actions mt-2 flex flex-wrap items-center gap-3">{actions}</div>;
    }

    // Otherwise render opinionated primary/secondary pattern
    if (primaryAction || secondaryAction) {
      return (
        <div className="empty-state-actions mt-2 flex flex-col gap-3">
          {primaryAction &&
            (primaryAction.href ? (
              <Button
                variant="secondary"
                size={buttonSize}
                onPress={primaryAction.onPress}
                isDisabled={primaryAction.isDisabled}
                isLoading={primaryAction.isLoading}
                href={primaryAction.href}
              >
                {primaryAction.label}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size={buttonSize}
                onPress={primaryAction.onPress}
                isDisabled={primaryAction.isDisabled}
                isLoading={primaryAction.isLoading}
              >
                {primaryAction.label}
              </Button>
            ))}
          {secondaryAction && (
            <TextLink
              href={secondaryAction.href || "#"}
              variant="muted"
              onPress={secondaryAction.onPress}
              className={typeRole("labelMd")}
            >
              {secondaryAction.label}
            </TextLink>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <section
      data-testid="empty-state"
      role="status"
      aria-live="polite"
      className={twMerge(
        "flex w-full flex-col rounded-xl text-text-primary",
        sizeStyles[size],
        alignmentStyles[alignment],
        fullHeight ? "h-full justify-center" : "",
        isSpotlight ? "relative overflow-hidden" : "",
        className
      )}
      style={spotlightStyle}
    >
      {icon && (
        <div
          className={twMerge(
            "flex items-center justify-center rounded-full border border-border-muted bg-background-muted text-text-secondary",
            iconContainerStyles[size]
          )}
        >
          <Icon name={icon} size={iconSizeMap[size]} />
        </div>
      )}

      <Heading size={titleSizeMap[size]} tag="h2" className="text-text-secondary">
        {title}
      </Heading>

      {description && (
        <p className={twMerge("max-w-xs text-text-secondary whitespace-pre-line", descriptionStyles[size])}>
          {description}
        </p>
      )}

      {renderActions()}
    </section>
  );
}
